import { OutagesByRange, Stream } from "@oculai/planner";
import { durationService } from "@oculai/planner";
import { DurationSettings } from "@oculai/planner/dist/services/durationService";
import {
  differenceInHours,
  eachDayOfInterval,
  endOfDay,
  format,
  millisecondsToHours,
  parse,
  startOfDay,
  getDay,
} from "date-fns";
import { ShortenedProcess } from "@/types/Process";
import { ProcessDetailDateItem, ProcessDetailSummary } from "@/types/Process";
import { ProjectDurationSettings } from "@/types/ProjectDurationSettings";

const calculateDurationSum = (processes: ShortenedProcess[]) => {
  const workTime = processes.reduce((accProcesses, process) => {
    const processWorkHours = process.work_intervals.reduce((accIntervals, interval) => {
      const period = interval.end_time.getTime() - interval.start_time.getTime();

      return accIntervals + period;
    }, 0);

    return accProcesses + processWorkHours;
  }, 0);

  return workTime;
};

const calculateDurationSumExcludingOverlaps = (processes: ShortenedProcess[]) => {
  processes.sort((a, b) => a.start_time.getTime() - b.start_time.getTime());
  const intervals: Record<string, [Date, Date][]> = {};
  let timeCount = 0;
  let lastDate = "";

  const calculateLastIntervalDuration = (workIntervals: [Date, Date][]) => {
    let lastTime = 0;

    const duration = workIntervals
      .sort((a, b) => a[0].getTime() - b[0].getTime())
      .reduce((acc, interval) => {
        const start = interval[0].getTime();
        const end = interval[1].getTime();

        if (lastTime > start && lastTime < end) {
          lastTime = end;
          return acc + end - lastTime;
        }

        if (lastTime < start) {
          lastTime = end;
          return acc + end - start;
        }

        return acc;
      }, 0);

    return duration;
  };

  processes.forEach((process) => {
    const date = process.start_time.toLocaleDateString();
    if (!intervals[date]) {
      if (lastDate) {
        timeCount += calculateLastIntervalDuration(intervals[lastDate]);
      }
      intervals[date] = [];
      lastDate = date;
    }

    const workIntervals = process.work_intervals.map<[Date, Date]>((interval) => [
      interval.start_time,
      interval.end_time,
    ]);

    intervals[date].push(...workIntervals);
  });

  if (lastDate) {
    timeCount += calculateLastIntervalDuration(intervals[lastDate]);
  }

  return timeCount;
};

const calculateWorkHoursSum = (processes: ShortenedProcess[], decimalHours = false) => {
  const workTime = processes.reduce((accProcesses, process) => {
    const processWorkHours = process.work_intervals.reduce((accIntervals, interval) => {
      const period =
        (interval.end_time.getTime() - interval.start_time.getTime()) *
        (interval.workforce?.validated_count || 0);

      return accIntervals + period;
    }, 0);

    return accProcesses + processWorkHours;
  }, 0);

  return decimalHours ? millisecondsToDecimalHours(workTime) : millisecondsToHours(workTime);
};

const millisecondsToDecimalHours = (milliseconds: number) => {
  const hours = milliseconds / (1000 * 60 * 60);
  return parseFloat(hours.toFixed(2));
};

const useCorrectOutages = (
  outagesByRange: OutagesByRange,
  streams: Stream[],
  nonWorkingDaysByDaySet: Set<string>,
  isWorkingDay: (date: Date) => boolean,
) => {
  const correctOutages = Object.fromEntries(
    Object.entries(outagesByRange).map(([key, outages]) => {
      const date = parse(key, "yyyy-MM-dd", new Date());
      const factor = 0.5;
      if (nonWorkingDaysByDaySet.has(key) || !isWorkingDay(date)) {
        return [key, []];
      }

      const out = outages.filter((entry) => {
        const outageHours = differenceInHours(entry.end_time, entry.start_time);
        const tmlHours = differenceInHours(entry.tml_end, entry.tml_start);
        return outageHours > tmlHours * factor;
      });
      return out.length > streams.length * factor ? [key, out] : [key, []];
    }),
  );

  return correctOutages;
};

const calculateOutages = (outages: OutagesByRange, processes: ShortenedProcess[]) => {
  const sortedProcesses = processes.slice().sort((a, b) => {
    if (a.start_time.getTime() === b.start_time.getTime()) {
      return a.end_time.getTime() - b.end_time.getTime();
    } else {
      return a.start_time.getTime() - b.start_time.getTime();
    }
  });

  const start = new Date(sortedProcesses.at(0)?.start_time || 0);
  const end = new Date(sortedProcesses.at(-1)?.end_time || 0);

  let outagesDuration = 0;

  const processesPerDate = sortedProcesses.reduce((acc, process) => {
    if (!acc[process.date]) {
      acc[process.date] = [];
    }

    acc[process.date].push(process);

    return acc;
  }, {} as Record<string, ShortenedProcess[]>);

  for (let date = new Date(start); date <= end; date.setDate(date.getDate() + 1)) {
    const stringDate = format(date, "yyyy-MM-dd");

    if (!processesPerDate[stringDate]?.length && outages[stringDate]?.length) {
      outagesDuration++;
    }
  }

  return outagesDuration;
};

const getWorkingDayCount = (date: Date, durationSettings: DurationSettings) => {
  const dayIndex = getDay(date);
  return durationSettings.workingDaysAndHoursMap[dayIndex].workingDays || 0;
};

const getCriticalNonWorkingDayByType = (
  date: Date,
  type: string,
  projectDurationSettings: ProjectDurationSettings,
) => {
  return projectDurationSettings.non_working_days
    .filter(
      (item) =>
        item.type === type &&
        item.is_critical &&
        startOfDay(item.start_date) <= date &&
        date <= endOfDay(item.end_date),
    )
    .map((item) => item.name);
};

const getNonCriticalNonWorkingDay = (
  date: Date,
  projectDurationSettings: ProjectDurationSettings,
) => {
  return projectDurationSettings.non_working_days
    .filter(
      (item) =>
        !item.is_critical && startOfDay(item.start_date) <= date && date <= endOfDay(item.end_date),
    )
    .map((item) => item.name);
};

const getFirstProcessDate = (processes: ShortenedProcess[]) => {
  return new Date(Math.min(...processes.map((p) => new Date(p.start_time).getTime())));
};

const getLastProcessDate = (processes: ShortenedProcess[]) => {
  return new Date(Math.max(...processes.map((p) => new Date(p.end_time).getTime())));
};

const getDateInterval = (processes: ShortenedProcess[]) => {
  return eachDayOfInterval({
    start: startOfDay(getFirstProcessDate(processes)),
    end: endOfDay(getLastProcessDate(processes)),
  });
};

const getProcessDetailMap = (
  processes: ShortenedProcess[],
  projectDurationSettings: ProjectDurationSettings,
  outages: OutagesByRange,
) => {
  const dateMap: Record<string, ProcessDetailDateItem> = {};

  const durationSettings = durationService.calculateSettings(
    projectDurationSettings.working_hours,
    projectDurationSettings.non_working_days,
  );

  const dateInterval = getDateInterval(processes);

  dateInterval.forEach((date) => {
    const formattedDate = format(date, "yyyy-MM-dd");
    const detail = {
      date: formattedDate,
      working_day_count: getWorkingDayCount(date, durationSettings),
      holidays: getCriticalNonWorkingDayByType(date, "public_holiday", projectDurationSettings),
      critical_disturbances: getCriticalNonWorkingDayByType(
        date,
        "disturbance",
        projectDurationSettings,
      ),
      non_critical_disturbances: getNonCriticalNonWorkingDay(date, projectDurationSettings),
      outages: outages[formattedDate] || [],
      processes: processes.filter((item) => item.date === formattedDate),
    };

    dateMap[formattedDate] = { ...detail, status: getStatusForDate(detail) };
  });

  return dateMap;
};

const getStatusForDate = (detail: ProcessDetailDateItem) => {
  // Ordered by priority
  if (detail.holidays.length > 0) {
    return "holiday";
  }
  if (detail.critical_disturbances.length > 0) {
    return "critical_disturbance";
  }
  if (detail.processes.length > 0) {
    return "active";
  }
  if (detail.outages.length > 0) {
    return "outage";
  }
  return "inactive";
};

const getProcessDetailSummary = (processDetailMap: Record<string, ProcessDetailDateItem>) => {
  const summary = {
    active: 0,
    holiday: 0,
    outage: 0,
    critical_disturbance: 0,
    inactive: 0,
  };

  Object.values(processDetailMap).forEach((date) => {
    if (date.status && date.status in summary) {
      summary[date.status] += date.working_day_count;
    }
  });

  return summary;
};

const getTotalDuration = (processDetailSummary: ProcessDetailSummary) => {
  return processDetailSummary.active + processDetailSummary.inactive + processDetailSummary.outage;
};

export default {
  millisecondsToHours,
  calculateWorkHoursSum,
  calculateDurationSum,
  calculateDurationSumExcludingOverlaps,
  calculateOutages,
  useCorrectOutages,
  getProcessDetailMap,
  getProcessDetailSummary,
  getFirstProcessDate,
  getLastProcessDate,
  getDateInterval,
  getTotalDuration,
};
